import { services } from "@/data/services";
import { ArrowRight, Layers, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { Button } from "@/components/ui/button";

import dienstenHeroBg from "@/assets/diensten-hero-bg.jpg";

const DienstenOverzicht = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[480px] overflow-hidden">
        <img
          src={dienstenHeroBg}
          alt="Modern IT-kantoor met meerdere schermen en netwerkapparatuur"
          loading="eager" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/25" />
        <div className="container relative z-10 flex min-h-[480px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar home
            </Link>

            <motion.div
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <Layers className="h-7 w-7 text-white" />
            </motion.div>

            <motion.h1
              className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Alle zakelijke IT-<span className="text-gradient">diensten</span> onder één dak
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Van internet en telefonie tot werkplekken en hardware — wij regelen je complete IT.
              Simpel, betrouwbaar en altijd voor een vast maandbedrag.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Maandelijks opzegbaar
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-blue" />
                Eén aanspreekpunt
              </span>
              <span className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-rinqel-green" />
                Geen kleine lettertjes
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service cards */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {services.map((service, index) => {
              const Icon = service.icon;
              const isAllesInEen = service.slug === "alles-in-een";
              return (
                <motion.div
                  key={service.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <Link
                    to={isAllesInEen ? "/contact" : `/diensten/${service.slug}`}
                    className={`group flex h-full flex-col rounded-xl border p-6 shadow-card transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1 ${
                      isAllesInEen
                        ? "border-primary/30 bg-hero-gradient text-primary-foreground"
                        : "border-border bg-card"
                    }`}
                  >
                    <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl ${
                      isAllesInEen
                        ? "bg-white/20"
                        : index % 3 === 0 ? "bg-hero-gradient" : index % 3 === 1 ? "bg-rinqel-blue-gradient" : "bg-rinqel-green-gradient"
                    }`}>
                      <Icon className="h-5 w-5 text-primary-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold">{service.title}</h3>
                    <p className={`mt-2 flex-1 text-sm leading-relaxed ${
                      isAllesInEen ? "text-primary-foreground/80" : "text-muted-foreground"
                    }`}>
                      {service.description}
                    </p>
                    <div className={`mt-4 flex items-center gap-1 text-sm font-semibold ${
                      isAllesInEen ? "text-primary-foreground" : "text-primary"
                    }`}>
                      {isAllesInEen ? "Neem contact op" : "Bekijk dienst"}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-muted/30 py-16">
        <div className="container text-center">
          <h2 className="text-2xl font-bold md:text-3xl">
            Wil je alles in één pakket?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            Bereken direct wat jouw IT-oplossing kost, of neem contact met ons op voor persoonlijk advies.
          </p>
          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button size="lg" asChild>
              <TrackedLink to="/configurator" trackLabel="Bereken je prijs" trackLocation="diensten_overzicht_cta">
                Bereken je prijs
              </TrackedLink>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <TrackedLink to="/contact" trackLabel="Neem contact op" trackLocation="diensten_overzicht_cta">
                Neem contact op
              </TrackedLink>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default DienstenOverzicht;
